<template>
  <a
    v-if="isExternalLink"
    :href="href"
    :class="buttonClasses"
    v-on="$listeners"
  >
    <slot />
  </a>

  <router-link
    v-else-if="href"
    :to="href"
    :class="buttonClasses"
    v-on="$listeners"
    @click.native="scrollFix(href)"
  >
    <slot />
  </router-link>

  <div
    v-else
    class="relative"
  >
    <button
      :disabled="loading || disabled"
      class="w-full"
      :class="buttonClasses"
      :type="type"
      v-on="$listeners"
    >
      <slot />
    </button>

    <div
      class="absolute left-0 top-0 w-full h-full items-center justify-center"
      :class="[loading ? 'flex' : 'hidden']"
    >
      <div class="spin_animation mr-2">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 157.383 157.467" v-bind:class="'w-7 h-7'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g fill="#7E7E7E"><path data-name="Path 94" d="M78.692 115.376a2.8 2.8 0 00-2.8 2.8v36.493a2.8 2.8 0 105.6 0v-36.496a2.806 2.806 0 00-2.8-2.797z"/><path data-name="Path 95" d="M78.692 0a2.806 2.806 0 00-2.8 2.8v36.495a2.8 2.8 0 105.6 0V2.8a2.808 2.808 0 00-2.8-2.8z"/><path data-name="Path 96" d="M42.07 78.734a2.8 2.8 0 00-2.8-2.8H2.799a2.8 2.8 0 000 5.6h36.474a2.8 2.8 0 002.797-2.8z"/><path data-name="Path 97" d="M154.587 75.932h-36.474a2.8 2.8 0 000 5.6h36.474a2.8 2.8 0 000-5.6z"/><path data-name="Path 98" d="M100.827 111.493a2.81 2.81 0 00-2.427-1.4 2.806 2.806 0 00-2.422 4.2l18.242 31.6a2.867 2.867 0 003.816 1.024 2.808 2.808 0 001.028-3.83z"/><path data-name="Path 99" d="M43.171 11.577a2.81 2.81 0 00-2.429-1.4 2.853 2.853 0 00-1.4.375 2.812 2.812 0 00-1.024 3.83l18.241 31.603a2.819 2.819 0 002.429 1.4 2.851 2.851 0 001.394-.375 2.809 2.809 0 001.024-3.83z"/><path data-name="Path 100" d="M46.972 97.057a2.8 2.8 0 00-3.823-1.028l-31.577 18.246a2.8 2.8 0 00-1.026 3.825 2.872 2.872 0 003.823 1.028l31.587-18.247a2.8 2.8 0 001.016-3.824z"/><path data-name="Path 101" d="M110.13 58.287a2.774 2.774 0 00.279 2.129 2.9 2.9 0 003.825 1.028l31.582-18.247a2.809 2.809 0 001.024-3.83 2.805 2.805 0 00-2.427-1.4 2.89 2.89 0 00-1.4.375L111.43 56.59a2.756 2.756 0 00-1.3 1.697z"/><path data-name="Path 102" d="M145.817 114.27l-31.591-18.241a2.775 2.775 0 00-1.389-.375 2.8 2.8 0 00-1.408 5.228l31.582 18.247a2.868 2.868 0 003.825-1.028 2.8 2.8 0 00-1.019-3.831z"/><path data-name="Path 103" d="M11.572 43.196l31.587 18.247a2.889 2.889 0 003.813-1.028 2.8 2.8 0 00-1.024-3.825L14.366 38.343a2.844 2.844 0 00-1.394-.375 2.8 2.8 0 00-2.708 3.527 2.773 2.773 0 001.308 1.701z"/><path data-name="Path 104" d="M60.382 110.469a2.763 2.763 0 00-1.394-.38 2.813 2.813 0 00-2.429 1.4l-18.237 31.6a2.809 2.809 0 001.024 3.83 2.874 2.874 0 003.825-1.024l18.235-31.6a2.8 2.8 0 00-1.024-3.825z"/><path data-name="Path 105" d="M97.009 47.007a2.891 2.891 0 003.82-1.024l18.237-31.606a2.8 2.8 0 10-4.844-2.8l-18.242 31.6a2.815 2.815 0 001.029 3.83z"/></g></svg>
      </div>
      <span class="loading-text text-center text-sm">Loading</span>
    </div>
  </div>
</template>

<script>
import { startsWith } from 'lodash'

import Vue from 'vue'
import getConfig from '~branding'

export default Vue.extend({
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (val) => ['primary', 'primary-border'].includes(val),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    href: {
      type: [String, Object],
      default: '',
    },
    type: {
      type: String,
      default: undefined,
    },
  },
  computed: {
    isExternalLink() {
      if (typeof (this.href) === 'string') {
        return startsWith(this.href, 'http')
      }
      return false
    },
    buttonClasses() {
      return [
        'btn inline-flex items-center justify-center h-12 px-6 uppercase font-medium leading-none transition-colors duration-300 ease-in-out border-2 rounded-3px text-center focus:outline-none',
        `btn-${this.variant}`,
        this.disabled ? 'disabled' : '',
        this.loading ? 'opacity-25 disabled' : '',
      ]
    },
    toHashtag() {
      if (typeof (this.href) === 'string') {
        return this.href.charAt(0) === '#'
      }
      return false
    },
  },
  methods: {
    // So that second use of the same hashtag triggers the url change (https://forum.vuejs.org/t/how-to-handle-anchors-bookmarks-with-vue-router/14563)
    scrollFix(hashtag) {
      if (this.toHashtag) {
        window.scrollTo(0, document.querySelector(hashtag).offsetTop - getConfig().header.offset)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.btn {
  font-size: 15px;
  letter-spacing: 1.5px;

  &:focus {
    position: relative;

    &::after {
      content: '';
      pointer-events: none;
      position: absolute;
      left: -6px;
      top: -6px;
      width: calc(100% + 12px);
      height: calc(100% + 12px);
      border: 1px solid black;
    }
  }
}

.btn-primary {
  @apply text-white bg-au_primary border-transparent;

  &:hover {
    @apply bg-au_primary_hover;
  }
}

.btn-primary-border {
  @apply text-au_primary bg-transparent border-au_primary bg-white;

  &:hover {
    @apply text-white bg-au_primary;
  }
}

button.disabled {
  @apply bg-au_gray-200 text-au_gray-400 border-au_gray-200 cursor-not-allowed;

  &:hover {
    @apply bg-au_gray-200 text-au_gray-400 border-au_gray-200;
  }
}

.spin_animation {
  svg {
    animation-name: spin;
    animation-duration: 1.5s;
    animation-iteration-count: infinite;
    transform-origin: 50% 50%;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
